import React from "react";

import moment from "moment";

import { ArrayUtils, formatNumber, useFertilizerHelpers, useGetFieldPractices, useInView, useTranslation } from "utils";

import { PRACTICE_SLUG, PracticeFCPO, ZERO_UUID } from "models";

import { Col, Icon, Row, SkeletonWrapper, Tooltip, Typography } from "components";

interface FertilizerTimelineProps {
  fieldId: string;
}

export const FertilizerTimeline = ({ fieldId }: FertilizerTimelineProps) => {
  const { getFertilizerName, getFertilizerNValue, mineralFertilizers, organicFertilizers } = useFertilizerHelpers();
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useTranslation("fieldTimeline");

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });

  // Generate 7 years starting from current year - 5, including 2026
  const currentYear = moment().year();
  const years = Array.from({ length: 7 }, (_, index) => currentYear - 5 + index);

  // Filter fertilizers from practices
  const fertilizers =
    fieldPractices?.practices?.filter(
      (practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS
    ) ?? [];

  // Group fertilizers by year
  const fertilizersByYear = ArrayUtils.groupBy(fertilizers, (fertilizer) => fertilizer?.year ?? "unknown");

  // Define fertilizer type order: MINERAL -> ORGANIC
  const fertilizerTypeOrder = [PRACTICE_SLUG.MINERAL_FERTILIZERS, PRACTICE_SLUG.ORGANIC_FERTILIZERS];

  // Function to sort fertilizers by type
  const sortFertilizersByType = (fertilizers: PracticeFCPO[]) =>
    fertilizers.sort((a, b) => {
      const aIndex = fertilizerTypeOrder.indexOf(a.type as typeof PRACTICE_SLUG.MINERAL_FERTILIZERS | typeof PRACTICE_SLUG.ORGANIC_FERTILIZERS);
      const bIndex = fertilizerTypeOrder.indexOf(b.type as typeof PRACTICE_SLUG.MINERAL_FERTILIZERS | typeof PRACTICE_SLUG.ORGANIC_FERTILIZERS);
      return aIndex - bIndex;
    });

  // Helper function to get detailed tooltip information
  const getFertilizerTooltipInfo = (fertilizer: PracticeFCPO) => {
    const name = getFertilizerName(fertilizer.type, fertilizer.assignedPracticeId, t("fieldTimeline:cropCard.unknown"));

    let tooltip = `${name}`;

    if (fertilizer.assignedPracticeId !== ZERO_UUID) {
      if (fertilizer.amount) {
        tooltip += `\nAmount: ${fertilizer.amount} ${fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS ? "kg/ha" : "m³/ha"}`;
      }

      const nValue = getFertilizerNValue(fertilizer);

      if (nValue !== null && nValue !== undefined) {
        tooltip += `\nN-Value: ${nValue}`;
      }
    }

    return tooltip;
  };

  // Calculate total nitrogen for a year
  const calculateYearNitrogen = (yearFertilizers: PracticeFCPO[]) =>
    yearFertilizers.reduce((total, fertilizer) => {
      // Skip ZERO_UUID fertilizers
      if (fertilizer.assignedPracticeId === ZERO_UUID) {
        return total;
      }

      const amount = fertilizer.amount ?? 0;

      if (fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
        // Mineral: amount × nValue
        const mineralFertilizer = mineralFertilizers?.find((f) => f.id === fertilizer.assignedPracticeId);
        const nValue = mineralFertilizer?.nValue ?? 0;
        return total + amount * nValue;
      }

      if (fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
        // Organic: amount × nValue × mineralEquivalent
        const organicFertilizer = organicFertilizers?.find((f) => f.id === fertilizer.assignedPracticeId);
        const nValue = fertilizer.nValue ?? 0;
        const mineralEquivalent = organicFertilizer?.mineralEquivalent ?? 0;
        return total + amount * nValue * mineralEquivalent;
      }

      return total;
    }, 0);

  return (
    <SkeletonWrapper isLoading={isLoading} type="block" height="9rem">
      <div ref={ref} className="practice-timeline">
        <Row>
          {/* prelint-ignore-col */}
          {years.map((year) => {
            const yearFertilizers = fertilizersByYear?.[year] || [];
            const sortedFertilizers = sortFertilizersByType(yearFertilizers);
            const totalNitrogen = calculateYearNitrogen(yearFertilizers);
            return (
              <Col key={year} className="practice-timeline-year">
                <div className="practice-year-header">
                  <Typography variant="paragraph-s">{year}</Typography>
                </div>
                <div className="practice-year-content">
                  {sortedFertilizers.length === 0 ? (
                    <div className="practice-item practice-empty">No fertilizers</div>
                  ) : (
                    <>
                      {sortedFertilizers.map((fertilizer) => (
                        <Tooltip key={fertilizer.id} title={getFertilizerTooltipInfo(fertilizer)} placement="top">
                          <div className={`practice-item ${fertilizer.verificationStatus || "unknown"}`}>
                            <div className="fertilizer-content">
                              <div>
                                <Icon
                                  iconName={
                                    fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS
                                      ? fertilizer.assignedPracticeId === ZERO_UUID
                                        ? "tabler/droplet-off"
                                        : "tabler/droplet"
                                      : "tabler/poo"
                                  }
                                  fill={fertilizer.assignedPracticeId !== ZERO_UUID ? "currentColor" : "none"}
                                  className="icon inline margin-right-25"
                                  size="s"
                                />
                                {getFertilizerName(fertilizer.type, fertilizer.assignedPracticeId, t("fieldTimeline:cropCard.unknown"))}
                              </div>
                              {fertilizer.assignedPracticeId !== ZERO_UUID &&
                                (() => {
                                  const nValue = getFertilizerNValue(fertilizer);
                                  return nValue !== null && nValue !== undefined ? <div className="paragraph-xs">N:{nValue}</div> : null;
                                })()}
                            </div>
                          </div>
                        </Tooltip>
                      ))}
                      <div className="practice-nitrogen-total">
                        <Typography variant="paragraph-s">Σ N: {formatNumber({ number: totalNitrogen, decimalPlaces: 2 })} kg N/ha</Typography>
                      </div>
                    </>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </SkeletonWrapper>
  );
};
